import { Component, TemplateFunction } from '@ribajs/core'
import { HistoryManager } from '@ribajs/router'

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS
  recover: boolean
  returnToUrl: string

  // METHODS
  showRecover: SbCustomerLoginComponent['showRecover']
  hideRecover: SbCustomerLoginComponent['hideRecover']
  toggleRecover: SbCustomerLoginComponent['toggleRecover']

  // TEMPLATES

  // INPUTS
  form: {
    customer: {
      email: string
      password: string
    }
  }
}

/**
 * A simple template for new component, just make a copy of this template, adjust the class / tag name and add a new export to `src/riba/components/index.ts`.
 */
export class SbCustomerLoginComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-customer-login foobar="hello">...</sb-customer-login>`
   */
  public static tagName = 'sb-customer-login'

  protected routerHistory = new HistoryManager()

  /**
   * Everything that is assigned to the scope can be used within the template with the Riba template engine.
   * Examples:
   * - Methods can be called with `<div rv-on-click="show"></div>`, this will call the show method defined in the scope
   * - Properties can be used with `<div rv-click-active="visible"></div>`, this will add the class `active` if visible is `true` and remove the class `active` if visible is `false`,
   * - Templates can be used with `<div rv-template="body"></div>`, `<div rv-html="body"></div>` or `<div rv-text="body"></div>`, this will be add the template, html or string to the innerHTML of the element. But you can only use the Riba Template Engine with `rv-template`
   */
  public scope: Scope = {
    // OPTIONS
    recover: false,
    returnToUrl: '/account',

    // METHODS
    showRecover: this.showRecover,
    hideRecover: this.hideRecover,
    toggleRecover: this.toggleRecover,

    // TEMPLATES

    // INPUTS
    form: {
      customer: {
        email: '',
        password: '',
      },
    },
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return []
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()
  }

  public showRecover(event?: Event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.scope.recover = true
  }

  public hideRecover(event?: Event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.scope.recover = false
  }

  public toggleRecover(event?: Event) {
    event?.preventDefault()
    event?.stopPropagation()
    this.scope.recover = !this.scope.recover
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCustomerLoginComponent.observedAttributes)
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  async beforeBind() {
    const prevState = this.routerHistory.prevStatus()
    if (prevState) {
      this.scope.returnToUrl = prevState.url
    }
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbCustomerLoginComponent.tagName}-ready`)
  }

  protected addEventListeners() {
    // Nothing yet
  }

  protected removeEventListeners() {
    // Nothing yet
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
